import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Loader2, ShieldCheck, CreditCard } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKS = [
  { key: "pack_start", credits: 400, price: "R$ 50,00", popular: false },
  { key: "pack_pro", credits: 1000, price: "R$ 125,00", popular: true },
  { key: "pack_mestre", credits: 2400, price: "R$ 300,00", popular: false },
];

const L = {
  pt: {
    eyebrow: "Compre créditos",
    h2a: "Recarregue e",
    h2b: "entre no jogo.",
    sub: "Pacotes de créditos com pagamento seguro via Stripe (cartão). Lembre-se: créditos viram produtos na loja — nunca saque em dinheiro.",
    buy: "Comprar agora",
    popular: "Mais popular",
    secure: "Pagamento seguro via Stripe",
    testnote: "Modo de testes: cartão 4242 4242 4242 4242, qualquer validade futura",
  },
  en: {
    eyebrow: "Buy credits",
    h2a: "Top up and",
    h2b: "get in the game.",
    sub: "Credit packs with secure Stripe checkout (card). Remember: credits become store products — never cash withdrawals.",
    buy: "Buy now",
    popular: "Most popular",
    secure: "Secure payment via Stripe",
    testnote: "Test mode: card 4242 4242 4242 4242, any future expiry",
  },
  es: {
    eyebrow: "Compra créditos",
    h2a: "Recarga y",
    h2b: "entra en el juego.",
    sub: "Paquetes de créditos con pago seguro vía Stripe (tarjeta). Recuerda: los créditos se canjean por productos — nunca retiros en dinero.",
    buy: "Comprar ahora",
    popular: "Más popular",
    secure: "Pago seguro vía Stripe",
    testnote: "Modo de pruebas: tarjeta 4242 4242 4242 4242, cualquier fecha futura",
  },
};

export default function CreditPacks() {
  const { lang } = useLang();
  const t = L[lang];
  const [loading, setLoading] = useState(null);

  const buy = async (lookupKey) => {
    if (loading) return;
    setLoading(lookupKey);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar pagamento");
      setLoading(null);
    }
  };

  return (
    <section id="creditos" data-testid="credit-packs" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute top-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-amber-500/[0.06] blur-[140px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="mono text-xs uppercase tracking-[0.3em] text-slate-500 mb-4"
          >
            {t.eyebrow}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight"
          >
            {t.h2a}{" "}
            <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">
              {t.h2b}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            {t.sub}
          </motion.p>
        </div>

        <div className="mt-14 grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {PACKS.map((p, i) => (
            <motion.div
              key={p.key}
              data-testid={`pack-card-${p.key}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -8 }}
              className={`relative rounded-3xl border p-7 text-center backdrop-blur transition-all duration-300 ${
                p.popular
                  ? "border-amber-400/50 bg-amber-400/[0.06] shadow-[0_20px_80px_-20px_rgba(251,191,36,0.3)]"
                  : "border-white/10 bg-[#0c1122]/80 hover:border-white/25"
              }`}
            >
              {p.popular && (
                <span className="mono absolute -top-3 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.2em] bg-amber-400 text-black font-bold rounded-full px-3.5 py-1.5">
                  {t.popular}
                </span>
              )}
              <Coins className={`w-8 h-8 mx-auto mb-4 ${p.popular ? "text-amber-300" : "text-sky-300"}`} />
              <p className="font-display font-black text-3xl text-white">
                {p.credits.toLocaleString(lang === "en" ? "en-US" : "pt-BR")}
              </p>
              <p className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                {lang === "en" ? "credits" : "créditos"}
              </p>
              <p className="font-display font-bold text-xl text-amber-300 mt-4">{p.price}</p>
              <button
                onClick={() => buy(p.key)}
                disabled={loading !== null}
                data-testid={`pack-buy-${p.key}`}
                className={`mt-6 w-full rounded-2xl font-display font-bold text-sm py-3.5 transition-all duration-200 active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2 ${
                  p.popular
                    ? "bg-gradient-to-r from-sky-400 via-blue-500 to-amber-400 text-black hover:scale-[1.03] hover:shadow-xl hover:shadow-amber-400/25"
                    : "border border-white/15 text-white hover:border-amber-400/50 hover:bg-white/5"
                }`}
              >
                {loading === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {t.buy}
              </button>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 flex flex-col items-center gap-2">
          <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-300" />
            {t.secure}
          </p>
          <p data-testid="packs-testnote" className="mono text-[10px] uppercase tracking-[0.14em] text-slate-600">
            {t.testnote}
          </p>
        </div>
      </div>
    </section>
  );
}
